import { HttpErrorResponse } from '@angular/common/http';

/**
 * Safely derives a human-readable message from a failed HTTP response.
 *
 * NgRx effects across core/state inline this shape:
 *
 *     catchError((err: HttpErrorResponse) => of(new XFault(err.error.message)))
 *
 * `HttpErrorResponse.error` is undefined whenever the failing response had no
 * parseable body - an empty 502 from a proxy, a timeout, a 204 - so that deref
 * throws. And a throw inside a catchError callback is NOT caught by that same
 * catchError: it escapes exhaustMap, reaches the outer actions$ stream and
 * terminates it, killing the effect for the rest of the session. The guard
 * against effect-death is defeated by the guard throwing.
 * (SAN-474 / SC-SAAS-FRONTEND-7R.)
 *
 * Always returning a string also stops `undefined` reaching a *Fault action,
 * which is what produced the `Fault( undefined )` Sentry groups
 * (SC-SAAS-FRONTEND-81 / -6Y, SAN-448).
 *
 * Deliberately NOT GlobalService.getErrorMessage(): that guards on a nested
 * `error.error` key and so silently drops a top-level `error.message`, and
 * reusing it would mean injecting GlobalService into 13 effect classes. Kept a
 * pure function so effects need no DI.
 *
 * Deliberately does NOT fall back to `err.message`: on an HttpErrorResponse
 * that is Angular's generated "Http failure response for <url>: 500 OK", which
 * is useful in a log and wrong in a user-facing toast. Fault messages surface
 * to users, so an empty body yields the generic fallback instead. The status
 * and URL are still on the HttpErrorResponse that Sentry captures.
 */
export function httpFaultMessage(
  err: HttpErrorResponse | any,
  fallback: string = 'Something went wrong. Please try again.'
): string {
  const body = err?.error;

  // Backend validation errors arrive as { error: ['first message', ...] }
  const nested = body?.error;
  if (Array.isArray(nested)) {
    const first = nested.find((m: unknown) => typeof m === 'string' && m.trim());
    if (first) {
      return first;
    }
  } else if (typeof nested === 'string' && nested.trim()) {
    return nested;
  }

  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message;
  }

  // Some endpoints return a bare string body rather than a JSON envelope.
  if (typeof body === 'string' && body.trim()) {
    return body;
  }

  return fallback;
}
